import axiosServices from "../utils/axios";

import type {
  ResolveSystemPromptParams,
  SystemPrompt,
  SystemPromptPayload,
} from "../utils/types/systemPrompts";

const normalizeSystemPrompt = (item: any): SystemPrompt => {
  const systemPromptId = item?.system_prompt_id ?? item?.systemPromptId ?? item?.id;

  return {
    ...item,
    id: systemPromptId,
    system_prompt_id: systemPromptId,
    company_id: item?.company_id ?? item?.companyId ?? null,
    person_role: item?.person_role ?? item?.personRole ?? null,
    company_type: item?.company_type ?? item?.companyType ?? null,
    created_dt: item?.created_dt ?? item?.createdDt ?? null,
    created_by: item?.created_by ?? item?.createdBy ?? null,
    modified_dt: item?.modified_dt ?? item?.modifiedDt ?? null,
    modified_by: item?.modified_by ?? item?.modifiedBy ?? null,
    active: item?.active ?? true,
  };
};

const extractList = (responseData: any): any[] => {
  const data = responseData?.data ?? responseData;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;

  return [];
};

export const fetchSystemPromptsList = async (params?: {
  active?: boolean;
  q?: string;
  tag?: string;
}) => {
  try {
    const response = await axiosServices.get("/system-prompts", { params });
    return extractList(response.data).map(normalizeSystemPrompt);
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to fetch system prompts",
    );
  }
};

const valuesMatch = (promptValue?: string | null, queryValue?: string | null) =>
  !!promptValue &&
  !!queryValue &&
  promptValue.trim().toLowerCase() === queryValue.trim().toLowerCase();

const findBestPromptMatch = (
  prompts: SystemPrompt[],
  params: ResolveSystemPromptParams,
) => {
  const activePrompts = prompts.filter(
    (prompt) => prompt.active && prompt.tag === params.tag,
  );

  const exactCompany = (prompt: SystemPrompt) =>
    !!params.company_id && prompt.company_id === params.company_id;
  const exactRole = (prompt: SystemPrompt) =>
    valuesMatch(prompt.person_role, params.person_role);
  const exactType = (prompt: SystemPrompt) =>
    valuesMatch(prompt.company_type, params.company_type);
  const generic = (prompt: SystemPrompt) =>
    !prompt.company_id && !prompt.person_role && !prompt.company_type;

  return (
    activePrompts.find(
      (prompt) => exactCompany(prompt) && (exactRole(prompt) || exactType(prompt)),
    ) ||
    activePrompts.find((prompt) => exactCompany(prompt)) ||
    activePrompts.find((prompt) => exactRole(prompt)) ||
    activePrompts.find((prompt) => exactType(prompt)) ||
    activePrompts.find((prompt) => generic(prompt)) ||
    activePrompts[0] ||
    null
  );
};

const resolveSystemPromptFromList = async (
  params: ResolveSystemPromptParams,
) => {
  const prompts = await fetchSystemPromptsList({
    active: true,
    tag: params.tag,
  });

  return findBestPromptMatch(prompts, params);
};

export const addSystemPrompt = async (data: SystemPromptPayload) => {
  try {
    const response = await axiosServices.post("/system-prompts", data);
    return normalizeSystemPrompt(response.data?.data);
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to add the new system prompt",
    );
  }
};

export const updateSystemPrompt = async (
  id: string,
  data: SystemPromptPayload,
) => {
  try {
    const response = await axiosServices.put(`/system-prompts/${id}`, data);
    return normalizeSystemPrompt(response.data?.data ?? { ...data, id });
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to update system prompt",
    );
  }
};

export const deleteSystemPrompt = async (id: string) => {
  try {
    const response = await axiosServices.delete(`/system-prompts/${id}`, {
      data: {
        forceDelete: true,
      },
      params: {
        forceDelete: true,
      },
    });
    if (response?.data && response.data.success === false) {
      throw new Error(response.data.message || "Failed to delete system prompt");
    }
    return { success: response?.data?.success, id };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to delete system prompt",
    );
  }
};

export const resolveSystemPrompt = async (
  params: ResolveSystemPromptParams,
) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  );
  try {
    const response = await axiosServices.get("/system-prompts/resolve", {
      params: cleanParams,
    });
    const prompt = response.data?.data;

    return prompt
      ? normalizeSystemPrompt(prompt)
      : resolveSystemPromptFromList(params);
  } catch (error) {
    console.error("System prompt resolver failed, falling back to list:", error);
    return resolveSystemPromptFromList(params);
  }
};
